import { Controller } from "@hotwired/stimulus"

// Full screen for a mounted board.
//
// Two different mechanisms can put this window in full screen and they are
// independent: F11 fullscreens the *browser*, requestFullscreen() fullscreens
// an *element*. Only the second matches the :fullscreen CSS pseudo-class. Left
// alone they stack — F11, then the button, and now it takes two exits to get
// back, having hidden the chrome once.
//
// So this controller owns one derived state instead: is the window effectively
// full screen, by either route. It stamps that on documentElement, and the
// button refuses to open a second layer over a first.
export default class extends Controller {
  connect() {
    this.boundSync = this.sync.bind(this)
    document.addEventListener("fullscreenchange", this.boundSync)
    window.addEventListener("resize", this.boundSync)
    this.sync()
  }

  disconnect() {
    document.removeEventListener("fullscreenchange", this.boundSync)
    window.removeEventListener("resize", this.boundSync)
  }

  toggle() {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else if (this.#browserFullscreen()) {
      // Already full screen via F11. Requesting it again would nest a second
      // layer the user then has to unwind twice, so leave it to F11 to undo.
      return
    } else {
      document.documentElement.requestFullscreen()
    }
  }

  // Only the real Fullscreen API hides the chrome. The F11 guess below is NOT
  // used here on purpose: it reads "viewport at least as tall as the screen",
  // and a viewport can exceed screen.height — headless, scaled or virtual
  // displays, unusual DPI. When it misfires the top bar collapses and the
  // navigation is gone, recoverable only by finding a 1px hover strip. That is
  // far worse than leaving the chrome up during an F11 session, so this errs
  // towards showing it.
  sync() {
    document.documentElement.classList.toggle(
      "ops-is-fullscreen", Boolean(document.fullscreenElement)
    )
  }

  // Deliberately only consulted to decide whether opening a *second* fullscreen
  // layer would stack. A false positive there costs nothing — the button simply
  // does nothing while F11 is already on — whereas the same guess driving what
  // is visible costs the user their navigation.
  #browserFullscreen() {
    return window.innerHeight >= screen.height - 2
  }
}
